import React, {useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {Screen, StatisticFilterDef} from '../types';
import {apiService} from '../services/api';
import {StatisticRecordPicker} from './StatisticRecordPicker';
import {ScreenDataTablePagination} from './ScreenDataTablePagination';
import {IconLock, IconPencil} from '@tabler/icons-react';
import {isClientAdmin} from '../utils/roles';
import {formatStatisticCell, initialStatisticFilterValues} from '../utils/statisticValues';
import {StatisticTableSkeleton} from './WidgetSkeleton';

interface StatisticViewProps {
    screen: Screen;
    /** Rendered inside a modal (builder preview) — hides the edit button. */
    embedded?: boolean;
}

/**
 * Tabular statistic. Reuses the screen visual language: v5 controls row with
 * the "Filtre" toggle, the grey bordered filter box (like the screen filter
 * builder), the card-table and the screens' pagination footer.
 */
export const StatisticView: React.FC<StatisticViewProps> = ({screen, embedded = false}) => {
    const navigate = useNavigate();
    const filters = useMemo(() => screen.statisticFilters ?? [], [screen]);

    const initialValues = useMemo(() => initialStatisticFilterValues(filters), [filters]);

    const [showFilters, setShowFilters] = useState(false);
    const [draftValues, setDraftValues] = useState<Record<string, string>>(initialValues);
    const [appliedValues, setAppliedValues] = useState<Record<string, string>>(initialValues);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const {data, isLoading, isFetching, error} = useQuery({
        queryKey: ['statisticData', screen.id, appliedValues],
        queryFn: ({signal}) => apiService.getStatisticData(screen.id, appliedValues, signal),
        // new filters keep this statistic's current table (under the spinner) instead of collapsing
        // the card; another statistic starts from the skeleton, never from the previous one's rows
        placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === screen.id ? previous : undefined),
    });

    const setValue = (code: string, value: string) => {
        setDraftValues((prev) => ({...prev, [code]: value}));
    };

    const applyFilters = () => {
        setPage(1);
        setAppliedValues({...draftValues});
    };

    const clearFilterValue = (filter: StatisticFilterDef) => {
        setValue(filter.code, '');
    };

    const renderFilterInput = (filter: StatisticFilterDef) => {
        const value = draftValues[filter.code] ?? '';
        // The api runs a locked filter on its default whatever is sent; the input only shows it
        const locked = filter.editable === false;

        switch (filter.type) {
            case 'date':
                return (
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={value}
                        disabled={locked}
                        onChange={(e) => setValue(filter.code, e.target.value)}
                    />
                );
            case 'boolean':
                return (
                    <select
                        className="form-select form-select-sm"
                        value={value}
                        disabled={locked}
                        onChange={(e) => setValue(filter.code, e.target.value)}
                    >
                        <option value="">—</option>
                        <option value="1">Áno</option>
                        <option value="0">Nie</option>
                    </select>
                );
            case 'integer':
                return (
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        value={value}
                        disabled={locked}
                        onChange={(e) => setValue(filter.code, e.target.value)}
                    />
                );
            case 'user':
            case 'many-to-one':
                return (
                    <StatisticRecordPicker
                        filter={filter}
                        value={value}
                        onChange={(next) => setValue(filter.code, next)}
                        disabled={locked}
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={value}
                        disabled={locked}
                        onChange={(e) => setValue(filter.code, e.target.value)}
                    />
                );
        }
    };

    const columns: string[] = data?.columns ?? [];
    const rows: Record<string, any>[] = data?.rows ?? [];
    const responseError = data && data.success === false ? data.error : null;

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pagedRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {/* Controls row — OUTSIDE card, above the table (v5 layout, same as screens) */}
            <div className="v5-controls">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 w-100">
                    <div className="flex-fill">
                        {screen.description && (
                            <span className="text-muted">{screen.description}</span>
                        )}
                    </div>
                    <div className="d-flex flex-column flex-md-row gap-2 ms-auto">
                        {filters.length > 0 && (
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                Filtre {showFilters ? '▲' : '▼'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filter card — same container as the screen filter builder */}
            {showFilters && (
                <div className="card mb-3">
                    <div className="card-body">
                        <div className="border rounded p-3 mb-2" style={{backgroundColor: 'rgb(248, 249, 250)'}}>
                            {filters.map((filter) => (
                                <div className="mb-2" key={filter.code}>
                                    <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
                                        <span className="small fw-medium" style={{flex: '0 0 200px'}}>
                                            {filter.displayName}
                                            {filter.required && ' *'}
                                            {filter.editable === false && (
                                                <span className="ms-1 text-muted" title="Filter sa nedá meniť" aria-label="Filter sa nedá meniť">
                                                    <IconLock size={14} className="align-text-bottom"/>
                                                </span>
                                            )}
                                        </span>
                                        <div style={{flex: '1 1 0', minWidth: 0}}>
                                            {renderFilterInput(filter)}
                                        </div>
                                        {filter.editable === false ? (
                                            <span style={{minWidth: '38px'}}/>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-ghost-danger"
                                                title="Vymazať hodnotu"
                                                style={{minWidth: '38px'}}
                                                onClick={() => clearFilterValue(filter)}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div className="d-flex flex-column flex-md-row gap-2 mt-3">
                                <button className="btn btn-sm btn-primary" onClick={applyFilters}>
                                    Aplikovať filtre
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!embedded && isClientAdmin() && (
                <div className="d-flex justify-content-end mb-2">
                    <button type="button" className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate(`/statistics/${screen.id}/edit`)}>
                        <IconPencil size={16} className="me-1"/>Upraviť štatistiku
                    </button>
                </div>
            )}

            {/* Table card — same as screens */}
            <div className="card" style={{position: 'relative'}}>
                {isFetching && !isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                    }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Načítavam...</span>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <StatisticTableSkeleton/>
                ) : error || responseError ? (
                    <div className="card-body">
                        <div className="alert alert-danger mb-0">
                            {responseError || 'Štatistiku sa nepodarilo načítať.'}
                        </div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="card-body">
                        <div className="alert alert-info mb-0">Žiadne dáta</div>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table card-table table-vcenter datatable">
                                <thead>
                                    <tr>
                                        {columns.map((column) => (
                                            <th key={column}>{column}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedRows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {columns.map((column) => (
                                                <td key={column}>{formatStatisticCell(row[column])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <ScreenDataTablePagination
                            totalRecords={rows.length}
                            pageSize={pageSize}
                            canGoPrev={currentPage > 1}
                            canGoNext={currentPage < totalPages}
                            onPrevPage={() => setPage(currentPage - 1)}
                            onNextPage={() => setPage(currentPage + 1)}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setPage(1);
                            }}
                            pageSizeOptions={[{limit: 20}, {limit: 50}, {limit: 100}]}
                            pageIndex={currentPage - 1}
                            rowsOnPage={pagedRows.length}
                        />
                    </>
                )}
            </div>

        </>
    );
};
